import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Mail, BarChart3, Calendar, User, Baby, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLE = {
  baixo: { color: "#10B981", bg: "bg-emerald-50", text: "text-emerald-700", label: "Baixo" },
  moderado: { color: "#F59E0B", bg: "bg-amber-50", text: "text-amber-700", label: "Moderado" },
  elevado: { color: "#EF4444", bg: "bg-red-50", text: "text-red-700", label: "Elevado" },
};

function formatIdade(meses) {
  const anos = Math.floor(meses / 12);
  const m = meses % 12;
  const parts = [];
  if (anos) parts.push(`${anos}a`);
  if (m) parts.push(`${m}m`);
  return parts.join(" ") || `${meses}m`;
}

export default function AdminTriagem() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(me.data);
        const [list, st] = await Promise.all([
          axios.get(`${API}/admin/triagem/list?limit=200`, { withCredentials: true }),
          axios.get(`${API}/admin/triagem/stats`, { withCredentials: true }),
        ]);
        setItems(list.data.items || []);
        setStats(st.data);
      } catch (e) {
        navigate("/admin/login", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/admin/login", { replace: true });
  };

  const exportCSV = () => {
    const header = ["Data", "Responsavel", "Email", "Crianca", "Idade (meses)", "Score", "Nivel", "Email Enviado"];
    const rows = items.map((r) => [
      new Date(r.created_at).toLocaleString("pt-BR"),
      r.nome_responsavel,
      r.email,
      r.nome_crianca || "",
      r.idade_meses,
      r.score,
      r.level,
      r.email_sent ? "Sim" : "Não",
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `triagem_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="admin-loading">
        <Loader2 className="w-5 h-5 animate-spin text-ga-emerald" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-triagem-page">
      {/* Topbar */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container-ga py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-ga-ink/60 hover:text-ga-ink">← Site</Link>
            <div className="h-4 w-px bg-border" />
            <nav className="flex items-center gap-3 text-sm">
              <span className="font-display font-black text-ga-ink">Triagem</span>
              <Link to="/admin/lembrancas" className="text-ga-ink/60 hover:text-ga-ink" data-testid="admin-nav-lembrancas">Lembranças</Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
            <div className="text-xs text-ga-ink/70 hidden sm:block">{user?.email}</div>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-ga-ink/70 hover:text-ga-ink" data-testid="admin-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="container-ga py-8 sm:py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
            <StatCard label="Total" value={stats.total} icon={BarChart3} color="#0F172A" />
            <StatCard label="Atenção baixa" value={stats.by_level?.baixo ?? 0} icon={BarChart3} color="#10B981" />
            <StatCard label="Atenção moderada" value={stats.by_level?.moderado ?? 0} icon={BarChart3} color="#F59E0B" />
            <StatCard label="Atenção elevada" value={stats.by_level?.elevado ?? 0} icon={BarChart3} color="#EF4444" />
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-ga-ink/70">{items.length} submissões listadas</div>
          <button onClick={exportCSV} className="btn-outline text-sm" data-testid="admin-export-csv">
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>

        {/* Table */}
        {error && <div className="text-sm text-red-700 mb-3">{error}</div>}
        <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="admin-triagem-table-wrapper">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background text-left text-xs uppercase tracking-wider text-ga-ink/60">
                <tr>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Responsável</th>
                  <th className="px-4 py-3">E-mail</th>
                  <th className="px-4 py-3">Criança</th>
                  <th className="px-4 py-3">Idade</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Nível</th>
                  <th className="px-4 py-3">E-mail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.length === 0 && (
                  <tr><td colSpan="8" className="px-4 py-10 text-center text-ga-ink/50">Sem submissões ainda.</td></tr>
                )}
                {items.map((r) => {
                  const lv = LEVEL_STYLE[r.level] || LEVEL_STYLE.baixo;
                  return (
                    <tr key={r.id} className="hover:bg-background/60 transition-colors" data-testid={`admin-row-${r.id}`}>
                      <td className="px-4 py-3 text-ga-ink/80 whitespace-nowrap">
                        <Calendar className="w-3.5 h-3.5 inline mr-1 opacity-50" />
                        {new Date(r.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-4 py-3 text-ga-ink font-medium">{r.nome_responsavel}</td>
                      <td className="px-4 py-3 text-ga-ink/70">{r.email}</td>
                      <td className="px-4 py-3 text-ga-ink/70">{r.nome_crianca || "—"}</td>
                      <td className="px-4 py-3 text-ga-ink/70">{formatIdade(r.idade_meses)}</td>
                      <td className="px-4 py-3 text-ga-ink/70 tabular-nums">{r.score}/20</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${lv.bg} ${lv.text}`}>{lv.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        {r.email_sent ? (
                          <Mail className="w-3.5 h-3.5 text-ga-emerald" />
                        ) : (
                          <Mail className="w-3.5 h-3.5 text-ga-ink/30" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ga-ink/60">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        {label}
      </div>
      <div className="mt-1.5 font-display font-black text-3xl text-ga-ink tabular-nums">{value}</div>
    </div>
  );
}
